"use client";

import { FC, useCallback, useContext, useMemo } from "react";
import {
    AssetsDirectory,
    CasinoGameTag,
    HrefBehavior,
    PopupTags,
    TranslationScopes,
} from "@finbackoffice/enums";
import { useSiteConfig } from "@finbackoffice/site-core";
import { ILayoutsSubHeaderConfig, ISiteConfigLayouts } from "@finbackoffice/fe-core";
import Button from "components/base/button/Button";
import { CasinoToolsContext } from "contexts";
import ActiveLink from "components/base/active-link/ActiveLink";
import { Svg } from "components/base/svg/Svg";
import { useDevice, useHrefBehavior, useLoadI18nextResource } from "hooks";
import Translate from "components/base/translate/Translate";
import styles from "./sub-header.module.sass";

const SubHeader: FC = () => {
    const siteLayoutsConfig = useSiteConfig<ISiteConfigLayouts>("layouts");
    const { isMobileDevice } = useDevice();

    const translationScopes = useMemo(() => [TranslationScopes.SubHeader], []);
    const { isTranslationsReady } = useLoadI18nextResource(translationScopes);

    const { handlePlayNow } = useContext(CasinoToolsContext);

    const { isGame, isLink } = useHrefBehavior();

    const onButtonClick = useCallback(
        (link: ILayoutsSubHeaderConfig) => {
            const href = link.href;

            const isSlots = link.href_behavior === HrefBehavior.GameSlot;
            const gameId = href ? `${href}${isMobileDevice ? "-mobile" : ""}` : "";

            handlePlayNow(gameId, isSlots);
        },
        [handlePlayNow, isMobileDevice],
    );

    const renderFlag = useCallback((link: ILayoutsSubHeaderConfig) => {
        switch (true) {
            case link.tags.includes(CasinoGameTag.New):
                return (
                    <span className={styles.new}>
                        <Translate tid="casino_new" namespace={TranslationScopes.Casino} />
                    </span>
                );
            case link.tags.includes(CasinoGameTag.Featured):
                return <span className={styles.live}>live</span>;
            case link.tags.includes(CasinoGameTag.Hot):
                return (
                    <Svg
                        src={`/${AssetsDirectory.Common}/${AssetsDirectory.Casino}/${AssetsDirectory.Filters}/hot.svg`}
                        className="svg-icon"
                        prefixAppName
                    />
                );
            case link.tags.includes(CasinoGameTag.Top):
                return (
                    <Svg
                        src={`/${AssetsDirectory.Common}/${AssetsDirectory.Casino}/${AssetsDirectory.Filters}/top.svg`}
                        className="svg-icon"
                        prefixAppName
                    />
                );
            case link.tags.includes(CasinoGameTag.Popular):
                return (
                    <Svg
                        src={`/${AssetsDirectory.Common}/${AssetsDirectory.Casino}/${AssetsDirectory.Filters}/popular.svg`}
                        className="svg-icon"
                        prefixAppName
                    />
                );
            default:
                return null;
        }
    }, []);

    const renderContent = useCallback(
        (link: ILayoutsSubHeaderConfig) => (
            <>
                {Boolean(link.tags.length) && renderFlag(link)}
                {link.icon_filename && (
                    <Svg
                        src={`/${AssetsDirectory.Common}/${AssetsDirectory.SubHeader}/${link.icon_filename}`}
                        className="svg-icon"
                        prefixAppName
                    />
                )}
                <Translate tid={`${link.id}_title`} namespace={TranslationScopes.SubHeader} />
            </>
        ),
        [renderFlag],
    );

    const renderLink = (link: ILayoutsSubHeaderConfig) => {
        if (isLink(link.href_behavior) && link.href) {
            if (link.href_behavior === HrefBehavior.Relative) {
                return (
                    <ActiveLink
                        key={link.id}
                        // onClick={linkClickHandler}
                        href={link.href}
                        activeClassName={styles.active}
                        className="banner-item">
                        {renderContent(link)}
                    </ActiveLink>
                );
            }
        } else if (isGame(link.href_behavior)) {
            return (
                <Button
                    key={link.id}
                    type="button"
                    onClick={() => onButtonClick(link)}
                    className="banner-item">
                    {renderContent(link)}
                </Button>
            );
        }
        return null;
    };

    if (!isTranslationsReady) {
        return null;
    }

    return (
        <div className={styles.subHeader}>
            <nav>
                {siteLayoutsConfig.subHeader.config
                    .filter((link) => link.scopes.some((scope) => scope === PopupTags.Allpages))
                    .map(renderLink)}
            </nav>
        </div>
    );
};

export default SubHeader;
